/** A client MCP diagnose result (backend `triage.diagnose.output` `RESULT_SCHEMA`; WPS P4-01..03):
 *  what the client got through `get_job`, or the AI-written answer the judge withheld.
 *
 *  Our words and theirs stay apart:
 *  - ours: the status and the partial reason (our sentence), the conclusion, each finding's
 *    severity, title, code and explanation (versioned templates), the evidence refs (our section
 *    titles);
 *  - written by AI: the answer, each finding's `why`, the gaps — quoted plain text
 *    (`UntrustedText` / `UntrustedInline`), never markup, never instructions.
 *
 *  A result is stored JSON: every part is read defensively. The client's links (their dashboard's)
 *  are never followed here. The whole result is also there as JSON, for what this view leaves out. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import { conclusionLabel, partialReasonLabel } from '@/api/mcpDiagnoseRuns';
import { UntrustedInline, UntrustedText } from '@/components/UntrustedText';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';
type Rec = Record<string, unknown>;

const isRecord = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
const records = (v: unknown): Rec[] => (Array.isArray(v) ? v.filter(isRecord) : []);
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : [];

/** Most important first, as the case file orders its findings. */
const SEVERITIES: Record<string, { label: string; color: ChipColor }> = {
  explains: { label: 'Explains', color: 'error' },
  warning: { label: 'Warning', color: 'warning' },
  info: { label: 'Info', color: 'info' },
  ok: { label: 'OK', color: 'success' },
};

function Finding({ f }: { f: Rec }) {
  const sev = SEVERITIES[str(f.severity)] ?? { label: str(f.severity) || '—', color: 'default' };
  const why = str(f.why);
  return (
    <Box component="li" data-finding={str(f.code)}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Chip size="small" color={sev.color} label={sev.label} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {str(f.title)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {str(f.code)}
        </Typography>
      </Stack>
      {str(f.explanation) && (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {str(f.explanation)}
        </Typography>
      )}
      {why && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="span" sx={{ mr: 1 }}>
            Why it matters (AI):
          </Typography>
          <UntrustedInline text={why} />
        </Box>
      )}
    </Box>
  );
}

export function DiagnosisView({ result }: { result: Rec | null | undefined }) {
  if (!isRecord(result)) {
    return (
      <Typography variant="body2" color="text.secondary">
        No answer yet: the run has not finished.
      </Typography>
    );
  }
  const status = str(result.status);
  const reason = str(result.partial_reason);
  const conclusion = str(result.conclusion);
  const findings = records(result.findings);
  const evidence = records(result.evidence);
  const gaps = strings(result.gaps);
  const answer = str(result.answer);

  return (
    <Stack spacing={2} data-diagnosis>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          color={status === 'complete' ? 'success' : 'warning'}
          label={status === 'complete' ? 'Complete' : status === 'partial' ? 'Partial' : status || '—'}
        />
        {reason && (
          <Typography variant="body2" color="text.secondary">
            {partialReasonLabel(reason)}
          </Typography>
        )}
        {conclusion && <Chip size="small" label={conclusionLabel(conclusion)} />}
      </Stack>
      {str(result.note) && <Typography variant="body2">{str(result.note)}</Typography>}

      {answer && <UntrustedText label="The answer (written by AI)" text={answer} />}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Findings
        </Typography>
        {findings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No finding was cited.
          </Typography>
        ) : (
          <Stack spacing={1.5} component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {findings.map((f, i) => (
              // Findings carry no id; their order is the result's.
              // eslint-disable-next-line react/no-array-index-key
              <Finding key={i} f={f} />
            ))}
          </Stack>
        )}
      </Box>

      {evidence.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Evidence read
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {evidence.map((e, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={i} component="li" variant="body2">
                {[str(e.title) || str(e.section), e.booking_id != null ? `booking #${str(e.booking_id)}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {gaps.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            What the evidence could not tell (AI)
          </Typography>
          <Stack spacing={0.5}>
            {gaps.map((g, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i}>
                <UntrustedInline text={g} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Mounted when opened: the JSON repeats every text above. */}
      <Accordion disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">The result as JSON</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component="pre"
            sx={{ m: 0, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {JSON.stringify(result, null, 2)}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
